import { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import api from 'services/api'
import useToast from 'hooks/useToast'
import { useAuth } from 'store/selectors'

// The phone beats at once when recording starts or stops, so a short poll
// shows the flip within seconds; only a crash waits for the watchdog grace.
// The read is one small Mongo query, cheap enough to run this often.
const POLL_MS = 5_000

const ON = '#4ade80'
const OFF = '#ef4444'
const NEVER = '#8b949e'

export const stateOf = (member) =>
  member.recording === true
    ? 'on'
    : member.recording === false
    ? 'off'
    : 'never'

const COLORS = { on: ON, off: OFF, never: NEVER }

export function ago(seconds) {
  if (seconds == null) return null
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`
  return `${Math.round(seconds / 86400)}d ago`
}

function firstName(member) {
  return (
    member.full_name?.split(' ')[0] ||
    (member.username ? `@${member.username}` : '?')
  )
}

export function describe(member) {
  const state = stateOf(member)
  if (state === 'never') return 'never sent a heartbeat'
  const beat = `last heartbeat ${ago(member.beat_age_s)}`
  if (state === 'on') return beat
  const stop = member.last_stop_reason
    ? `stopped: ${member.last_stop_reason} (${ago(member.last_stop_age_s)})`
    : null
  return [beat, stop].filter(Boolean).join(' · ')
}

const Chip = ({ state }) => {
  const label =
    state === 'on' ? 'mic on' : state === 'off' ? 'mic off' : 'no beat'
  const filled = state === 'off'
  return (
    <Box
      sx={{
        mt: 1,
        px: 1.25,
        py: 0.25,
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.6,
        whiteSpace: 'nowrap',
        color: filled ? '#fff' : COLORS[state],
        bgcolor: filled ? OFF : 'rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {state === 'on' && (
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ON }}
        />
      )}
      {label}
    </Box>
  )
}

const Member = ({ member, onPoke, poking }) => {
  const state = stateOf(member)
  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: '0.8rem' }}>
          <Box sx={{ fontWeight: 700 }}>
            {member.full_name || member.username}
          </Box>
          <Box>{describe(member)}</Box>
          <Box sx={{ mt: 0.5, opacity: 0.7 }}>click to poke</Box>
        </Box>
      }
      arrow
    >
      <Box
        onClick={() => !poking && onPoke(member)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: poking ? 'default' : 'pointer',
          width: 104,
          '&:hover .avatar': { transform: 'scale(1.05)' },
        }}
      >
        <Box
          className="avatar"
          sx={{
            p: '3px',
            borderRadius: '50%',
            border: `3px solid ${COLORS[state]}`,
            transition: 'transform 120ms ease',
            position: 'relative',
            opacity: state === 'never' ? 0.6 : 1,
          }}
        >
          <Avatar
            src={member.picture || undefined}
            alt={member.full_name || member.username || ''}
            sx={{ width: 72, height: 72, fontSize: '1.6rem' }}
          >
            {firstName(member)[0]?.toUpperCase()}
          </Avatar>
          {poking && (
            <CircularProgress
              size={84}
              thickness={2}
              sx={{ position: 'absolute', top: -3, left: -3, color: '#fff' }}
            />
          )}
        </Box>
        <Chip state={state} />
        <Box
          sx={{
            mt: 0.5,
            fontSize: '0.85rem',
            fontWeight: 600,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {firstName(member)}
        </Box>
      </Box>
    </Tooltip>
  )
}

const MicRail = () => {
  const { user } = useAuth()
  const toast = useToast()
  const [presence, setPresence] = useState(null)
  const [error, setError] = useState(null)
  const [target, setTarget] = useState(null)
  const [poking, setPoking] = useState(null)

  const refresh = useCallback(async () => {
    try {
      setPresence(await api.staff.getPresence())
      setError(null)
    } catch (err) {
      setError(err.message)
    }
  }, [])

  useEffect(() => {
    refresh()
    const timer = setInterval(refresh, POLL_MS)
    return () => clearInterval(timer)
  }, [refresh])

  const poke = useCallback(async () => {
    const member = target
    setTarget(null)
    setPoking(member.id)
    try {
      const { sent, reason } = await api.staff.poke(member.id)
      toast({
        message: sent
          ? `Poked ${firstName(member)}.`
          : `${firstName(member)} was not poked: ${reason}.`,
        severity: sent ? 'success' : 'warning',
      })
    } catch (err) {
      toast({ message: err.message, severity: 'error' })
    } finally {
      setPoking(null)
    }
  }, [target, toast])

  const members = presence?.members || []
  const on = members.filter((m) => m.recording === true).length
  const me =
    user?.full_name || (user?.username ? `@${user.username}` : 'someone')

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box sx={{ fontSize: '1.4em', fontWeight: 700 }}>Staff Mics</Box>
        <Box sx={{ fontSize: '0.9em', opacity: 0.7 }}>
          {presence ? `${on} of ${members.length} on` : 'loading'}
          {error ? ` · ${error}` : ''}
        </Box>
      </Box>
      <Box sx={{ fontSize: '0.9em', opacity: 0.7, mb: 2 }}>
        Mic on means the phone sent a heartbeat in the last{' '}
        {presence ? Math.round(presence.fresh_seconds / 60) : '…'} minutes while
        recording. Click someone to poke their phone.
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          p: 3,
          minHeight: 160,
          borderRadius: 3,
          bgcolor: '#1b1b1b',
          color: '#fff',
        }}
      >
        {members.map((member) => (
          <Member
            key={member.id}
            member={member}
            poking={poking === member.id}
            onPoke={setTarget}
          />
        ))}
      </Box>
      <Dialog open={Boolean(target)} onClose={() => setTarget(null)}>
        <DialogTitle>Poke {target && firstName(target)}?</DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: '0.95em' }}>Their phone will show:</Box>
          <Box
            sx={{
              mt: 1.5,
              p: 2,
              borderRadius: 2,
              bgcolor: 'background.default',
              fontStyle: 'italic',
            }}
          >
            hey, {me} poked you. turn on your mic!
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={poke}>
            Poke
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MicRail
